import { Component, OnDestroy, OnInit } from "@angular/core";
import { CommonModule } from "@angular/common";
import { MatProgressSpinnerModule } from "@angular/material/progress-spinner";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { environment } from "environment";
import { TherapyReferralEmbedConstants } from "./therapy-referral-embed.constants";

const LOAD_TIMEOUT_MS = 20000;

@Component({
  selector: "app-therapy-referral-embed",
  standalone: true,
  imports: [CommonModule, MatProgressSpinnerModule],
  templateUrl: "./therapy-referral-embed.component.html",
  styleUrls: ["./therapy-referral-embed.component.scss"],
})
export class TherapyReferralEmbedComponent implements OnInit, OnDestroy {
  readonly labels = TherapyReferralEmbedConstants;

  sanitizedUrl: SafeResourceUrl | null = null;
  isLoading = true;
  timedOut = false;
  errorMessage: string | null = null;

  private url: string | null = null;
  private fullAppUrl: string | null = null;
  private loadTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(private sanitizer: DomSanitizer) {}

  ngOnInit(): void {
    this.url = environment.therapyReferralUrl || null;
    this.fullAppUrl = environment.therapyReferralFullAppUrl || null;
    if (!this.url) {
      this.isLoading = false;
      this.errorMessage = TherapyReferralEmbedConstants.notConfiguredMessage;
      return;
    }
    this.loadFrame();
  }

  ngOnDestroy(): void {
    this.clearTimers();
  }

  get canOpenFullApp(): boolean {
    return !!this.fullAppUrl;
  }

  onIframeLoad(): void {
    this.clearTimers();
    this.isLoading = false;
    this.timedOut = false;
  }

  refresh(): void {
    // Force the iframe out of the DOM (via *ngIf) for a tick before recreating it - setting
    // the same src string back-to-back in one change-detection cycle wouldn't cause a real
    // browser navigation.
    this.sanitizedUrl = null;
    setTimeout(() => this.loadFrame(), 0);
  }

  openFullApp(): void {
    if (this.fullAppUrl) {
      window.open(this.fullAppUrl, "_blank", "noopener,noreferrer");
    }
  }

  private loadFrame(): void {
    if (!this.url) return;
    this.clearTimers();
    this.isLoading = true;
    this.timedOut = false;
    this.sanitizedUrl = this.sanitizer.bypassSecurityTrustResourceUrl(this.url);
    this.loadTimeout = setTimeout(() => {
      this.isLoading = false;
      this.timedOut = true;
      this.logLoadIssue("timeout");
    }, LOAD_TIMEOUT_MS);
  }

  private clearTimers(): void {
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
      this.loadTimeout = null;
    }
  }

  private logLoadIssue(reason: "timeout"): void {
    if (window._paq) {
      window._paq.push(["trackEvent", "Therapy Referrals", "Embed Load Issue", reason]);
    }
  }
}
